import { cbSafe } from "../lib/colourBlind";
import React, { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { useSearchParams } from "react-router-dom";
import { MapPin, Globe, Shapes, ExternalLink } from "lucide-react";
import { floatingSheet, theme, inputStyle } from "../theme";
import { api, getViewingOrgId } from "../api/client";
import { DeptChip } from "../pages/Departments";

// ── One tag row for everything on the platform ────────────────────────────────
//
// The platform asks the same four questions of a control, a risk, a supplier, an
// asset, an incident, a RoPA entry and a corrective action: who owns it
// (department), where is it (office), whose law governs it (jurisdiction) and
// what kind of thing is it (type). This component is the one place any of them
// is answered, so a register, an assessment page and the site detail page all
// show and filter the same tags in the same shape — and the asset register's
// comma-separated free-text box, which was the platform's one other idea about
// tagging, is gone.
//
// The mirror is backend/src/lib/entityTags.ts. The data is fetched once per
// organisation and shared by every card on the page through a small external
// store, so a framework page with 93 controls costs one request, not one per
// card. Tagging is filing: it has never changed what anyone can see or do.

// ── Types (mirror of lib/entityTags.ts) ───────────────────────────────────────

export type TagDimension = "department" | "site" | "jurisdiction" | "type";
export const ALL_TAG_DIMENSIONS: readonly TagDimension[] = ["department", "site", "jurisdiction", "type"];

export type EntityKind =
  | "control" | "risk" | "asset" | "supplier" | "incident" | "exercise" | "training"
  | "document" | "ropa" | "dpia" | "breach" | "request" | "action" | "ai_system" | "task" | "bia"
  | "ms_context" | "ms_objective" | "ms_audit" | "ms_review" | "ms_competence" | "ms_aspect" | "ms_hazard" | "ms_obligation"
  | "change";

export interface EntityRef { kind: EntityKind; id: string }

/**
 * The registers that already own a `siteId` column (mirror of lib/entityTags.ts
 * SITE_COLUMN_KINDS). For these the Office dimension **is** that column — the
 * one the site detail page and `?siteId=` already read — so the picker replaces
 * rather than adds, and nothing can hold two different answers to "which
 * office". Every other kind stores offices as tags and may carry several.
 */
export const SITE_COLUMN_KINDS: ReadonlySet<EntityKind> = new Set<EntityKind>([
  "risk", "asset", "incident", "document", "task",
]);

/** A control keeps its bare id as its ref — that is what every tag written before this existed holds. */
export function entityRef(ref: EntityRef): string {
  return ref.kind === "control" ? ref.id : `${ref.kind}:${ref.id}`;
}

export interface Dept { id: string; name: string; color: string }
export interface SiteRef { id: string; name: string; country: string }
export interface TypeSpec { value: string; label: string; hint: string; color: string }

export type EntityTagSet = Record<TagDimension, string[]>;

const EMPTY_SET: EntityTagSet = { department: [], site: [], jurisdiction: [], type: [] };

interface TagState {
  orgKey: string | null;
  loaded: boolean;
  departments: Dept[];
  sites: SiteRef[];
  countries: string[];
  types: TypeSpec[];
  /** Every tag the organisation holds, by entity ref. */
  tags: Record<string, EntityTagSet>;
  /** The entity kinds this user may tag; "+ Tag" is hidden on the rest. */
  writableKinds: EntityKind[];
}

const EMPTY: TagState = {
  orgKey: null, loaded: false, departments: [], sites: [], countries: [], types: [], tags: {}, writableKinds: [],
};

let state: TagState = EMPTY;
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();

function emit(next: TagState) {
  state = next;
  for (const l of listeners) l();
}
function subscribe(l: () => void) { listeners.add(l); return () => { listeners.delete(l); }; }
function snapshot() { return state; }

function orgKey() { return getViewingOrgId() ?? "self"; }

async function load(force = false): Promise<void> {
  const key = orgKey();
  if (!force && state.loaded && state.orgKey === key) return;
  if (inflight && state.orgKey === key) return inflight;
  inflight = (async () => {
    // One request for every vocabulary and every tag. A caller who may not read
    // the organisation's structure simply gets empty lists and no picker.
    const d = await api.get<{
      departments: Dept[]; sites: SiteRef[]; countries: string[]; types: TypeSpec[];
      tags: Record<string, EntityTagSet>; writableKinds: EntityKind[];
    }>("/tags").catch(() => null);
    emit(d
      ? { orgKey: key, loaded: true, departments: d.departments ?? [], sites: d.sites ?? [], countries: d.countries ?? [],
          types: d.types ?? [], tags: d.tags ?? {}, writableKinds: d.writableKinds ?? [] }
      : { ...EMPTY, orgKey: key, loaded: true });
  })().finally(() => { inflight = null; });
  emit({ ...state, orgKey: key });
  return inflight;
}

/** Everything the tag row and filters need; loads on first use, once per organisation. */
export function useEntityTagData(): TagState & { refresh: () => Promise<void> } {
  const s = useSyncExternalStore(subscribe, snapshot, snapshot);
  useEffect(() => { void load(); }, []);
  return { ...s, refresh: () => load(true) };
}

/** Back-compatible alias — the assessment pages have always called it this. */
export const useControlTagData = useEntityTagData;

export function tagsOf(data: Pick<TagState, "tags">, ref: string): EntityTagSet {
  return data.tags[ref] ?? EMPTY_SET;
}

/** Adds or removes one tag, optimistically; a refused write puts it back. */
export async function toggleEntityTag(ref: string, dimension: TagDimension, value: string, single = false): Promise<void> {
  const current = tagsOf(state, ref);
  const next = current[dimension].includes(value)
    ? current[dimension].filter((v) => v !== value)
    : single ? [value] : [...current[dimension], value];
  const set = (values: string[]) => emit({
    ...state,
    tags: { ...state.tags, [ref]: { ...current, [dimension]: values } },
  });
  set(next);
  try {
    await api.put(`/tags/${encodeURIComponent(ref)}`, { [dimension]: next });
  } catch {
    set(current[dimension]);
  }
}

/** The same, for a control id — kept so the assessment pages need no change. */
export function toggleControlTag(dimension: TagDimension, controlId: string, value: string): Promise<void> {
  return toggleEntityTag(controlId, dimension, value);
}

// ── Filter ────────────────────────────────────────────────────────────────────

export type TagFilter = Partial<Record<TagDimension, string>>;
export const EMPTY_TAG_FILTER: TagFilter = { department: "", site: "", jurisdiction: "", type: "" };

/** Reads ?department= ?site= ?jurisdiction= ?type= so one register can open another on its own tags. */
export function useTagFilter(): [TagFilter, (f: TagFilter) => void] {
  const [params] = useSearchParams();
  const [filter, setFilter] = useState<TagFilter>(() => ({
    department: params.get("department") ?? "",
    site: params.get("site") ?? "",
    jurisdiction: params.get("jurisdiction") ?? "",
    type: params.get("type") ?? "",
  }));
  return [filter, setFilter];
}

export function isTagFilterActive(f: TagFilter): boolean {
  return ALL_TAG_DIMENSIONS.some((d) => !!f[d]);
}

/** True when the entity carries every tag the filter names. */
export function matchesEntityTagFilter(data: Pick<TagState, "tags">, ref: string, f: TagFilter): boolean {
  const set = data.tags[ref];
  for (const dimension of ALL_TAG_DIMENSIONS) {
    const wanted = f[dimension];
    if (!wanted) continue;
    if (!set || !set[dimension].includes(wanted)) return false;
  }
  return true;
}

/** The same, keyed by control id, for the assessment pages. */
export const matchesTagFilter = matchesEntityTagFilter;

/** A sentence for the "N match" line, naming what is filtered on. */
export function describeTagFilter(data: TagState, f: TagFilter): string {
  const parts: string[] = [];
  if (f.department) parts.push(data.departments.find((d) => d.id === f.department)?.name ?? "department");
  if (f.site) parts.push(data.sites.find((s) => s.id === f.site)?.name ?? "site");
  if (f.jurisdiction) parts.push(f.jurisdiction);
  if (f.type) parts.push(data.types.find((t) => t.value === f.type)?.label ?? "type");
  return parts.join(" · ");
}

export function EntityTagFilters({ value, onChange, accentColor, dimensions = ALL_TAG_DIMENSIONS }: {
  value: TagFilter;
  onChange: (next: TagFilter) => void;
  accentColor?: string;
  dimensions?: readonly TagDimension[];
}) {
  const data = useEntityTagData();
  const accent = accentColor ?? theme.color.accent;
  const select = (active: boolean): React.CSSProperties => ({
    ...inputStyle, width: "auto", padding: "6px 10px", fontSize: 12, cursor: "pointer",
    border: `1px solid ${active ? accent : theme.color.borderStrong}`,
    color: active ? accent : theme.color.subtle,
    background: active ? accent + "11" : theme.color.panel,
  });
  return (
    <>
      {dimensions.includes("department") && data.departments.length > 0 && (
        <select value={value.department ?? ""} onChange={(e) => onChange({ ...value, department: e.target.value })} style={select(!!value.department)} title="Owned by one department">
          <option value="">All departments</option>
          {data.departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      )}
      {dimensions.includes("site") && data.sites.length > 0 && (
        <select value={value.site ?? ""} onChange={(e) => onChange({ ...value, site: e.target.value })} style={select(!!value.site)} title="Specific to one office">
          <option value="">All offices</option>
          {data.sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      )}
      {dimensions.includes("jurisdiction") && data.countries.length > 1 && (
        <select value={value.jurisdiction ?? ""} onChange={(e) => onChange({ ...value, jurisdiction: e.target.value })} style={select(!!value.jurisdiction)} title="Tagged to one jurisdiction">
          <option value="">All jurisdictions</option>
          {data.countries.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      )}
      {dimensions.includes("type") && data.types.length > 0 && (
        <select value={value.type ?? ""} onChange={(e) => onChange({ ...value, type: e.target.value })} style={select(!!value.type)} title="What kind of thing this is about">
          <option value="">All types</option>
          {data.types.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      )}
    </>
  );
}

/** Back-compatible alias — the assessment pages have always called it this. */
export const ControlTagFilters = EntityTagFilters;

// ── Chips ─────────────────────────────────────────────────────────────────────

const chipBase: React.CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 3, fontSize: 11, fontWeight: 600,
};

function SiteChip({ site }: { site: SiteRef }) {
  return (
    <span title={`At ${site.name}, ${site.country}`} style={{ ...chipBase, background: theme.color.accentBg, color: theme.color.accentText, border: `1px solid ${theme.color.accent}33` }}>
      <MapPin size={10} />{site.name}
    </span>
  );
}

function JurisdictionChip({ country }: { country: string }) {
  return (
    <span title={`Applies in ${country}`} style={{ ...chipBase, background: theme.color.panelAlt, color: theme.color.muted, border: `1px solid ${theme.color.border}` }}>
      <Globe size={10} />{country}
    </span>
  );
}

export function TypeChip({ spec }: { spec: TypeSpec }) {
  return (
    <span title={spec.hint} style={{ ...chipBase, background: cbSafe(spec.color + "22"), color: cbSafe(spec.color), border: `1px solid ${cbSafe(spec.color)}55` }}>
      <Shapes size={10} />{spec.label}
    </span>
  );
}

function PickerSection({ title, items, checked, onToggle, empty, suggested }: {
  title: string;
  items: Array<{ value: string; label: string; hint?: string; color?: string }>;
  checked: string[];
  onToggle: (value: string) => void;
  empty: string;
  suggested?: readonly string[];
}) {
  return (
    <div style={{ minWidth: 180 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.05em", padding: "2px 10px 4px" }}>{title}</div>
      {items.length === 0 && <div style={{ fontSize: 11, color: theme.color.faint, padding: "2px 10px 6px", maxWidth: 200 }}>{empty}</div>}
      {items.map((it) => (
        <label
          key={it.value}
          title={it.hint}
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 10px", cursor: "pointer", borderRadius: 3, fontSize: 12, color: theme.color.text }}
          onMouseEnter={(e) => (e.currentTarget.style.background = theme.color.panelAlt)}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        >
          <input type="checkbox" checked={checked.includes(it.value)} onChange={() => onToggle(it.value)} style={{ cursor: "pointer", accentColor: it.color }} />
          {it.color && <span style={{ width: 8, height: 8, borderRadius: "50%", background: cbSafe(it.color), flexShrink: 0 }} />}
          <span>{it.label}</span>
          {/* The row's own classification implies this one — offered, never applied behind anyone's back. */}
          {suggested?.includes(it.value) && !checked.includes(it.value) && (
            <span style={{ fontSize: 9, color: theme.color.faint, border: `1px dashed ${theme.color.border}`, borderRadius: 3, padding: "0 4px" }}>suggested</span>
          )}
        </label>
      ))}
    </div>
  );
}

// ── The row ───────────────────────────────────────────────────────────────────

export interface EntityTagsRowProps {
  entity: EntityRef;
  accentColor?: string;
  readOnly?: boolean;
  dimensions?: readonly TagDimension[];
  /**
   * The row's own classification, used only to pre-suggest a type in the picker
   * — `Asset.kind` for an asset. Nothing is written from it.
   */
  classifier?: string | null;
  style?: React.CSSProperties;
  /** Legacy free-text values still on the row, shown so nothing the organisation typed disappears. */
  legacyTags?: string[];
}

/**
 * The chips an entity carries plus, for anyone who may edit its register, one
 * "+ Tag" button opening a picker with a section per dimension. Renders nothing
 * when the organisation has nothing to tag with (and, read-only, when the entity
 * carries no tag at all).
 */
function collapsedChip(color: string): React.CSSProperties {
  return {
    padding: "2px 8px", borderRadius: 3, fontSize: 11, cursor: "pointer", fontFamily: theme.font,
    border: `1px dashed ${theme.color.borderStrong}`, background: "transparent", color,
  };
}

export function EntityTagsRow({ entity, accentColor, readOnly, dimensions = ALL_TAG_DIMENSIONS, classifier, style, legacyTags }: EntityTagsRowProps) {
  const data = useEntityTagData();
  const [open, setOpen] = useState(false);
  // The chips themselves, rather than the one collapsed "Tags (n)" chip.
  const [expanded, setExpanded] = useState(false);
  const [suggested, setSuggested] = useState<string[]>([]);
  const accent = accentColor ?? theme.color.accent;
  const ref = useMemo(() => entityRef(entity), [entity.kind, entity.id]);
  const canEdit = !readOnly && data.writableKinds.includes(entity.kind);
  // These registers keep the office in their own siteId column, which holds one.
  const singleSite = SITE_COLUMN_KINDS.has(entity.kind);

  // Asked for only when the picker opens, and only where the answer can differ.
  useEffect(() => {
    if (!open || suggested.length) return;
    if (entity.kind !== "asset" && !classifier) { setSuggested([]); return; }
    api.get<{ types: string[] }>(`/tags/suggest/${encodeURIComponent(ref)}`)
      .then((d) => setSuggested(d.types ?? []))
      .catch(() => { /* a suggestion is a convenience, never a blocker */ });
  }, [open, ref]);

  const showDept = dimensions.includes("department") && data.departments.length > 0;
  const showSite = dimensions.includes("site") && data.sites.length > 0;
  const showJurisdiction = dimensions.includes("jurisdiction") && data.countries.length > 0;
  const showType = dimensions.includes("type") && data.types.length > 0;
  if (!showDept && !showSite && !showJurisdiction && !showType) return null;

  const set = tagsOf(data, ref);
  const deptIds = showDept ? set.department : [];
  const siteIds = showSite ? set.site : [];
  const countries = showJurisdiction ? set.jurisdiction : [];
  const typeKeys = showType ? set.type : [];
  const depts = deptIds.map((id) => data.departments.find((d) => d.id === id)).filter(Boolean) as Dept[];
  const sites = siteIds.map((id) => data.sites.find((s) => s.id === id)).filter(Boolean) as SiteRef[];
  const types = typeKeys.map((k) => data.types.find((t) => t.value === k)).filter(Boolean) as TypeSpec[];
  const total = depts.length + sites.length + countries.length + types.length + (legacyTags?.length ?? 0);
  if (!canEdit && total === 0) return null;

  // Collapsed to one chip until asked for: a row of chips on every card was
  // detail most people never read. The names stay in the chip's tooltip.
  if (!expanded && !open) {
    const names = [
      ...types.map((t) => t.label), ...depts.map((d) => d.name), ...sites.map((s) => s.name), ...countries, ...(legacyTags ?? []),
    ];
    if (total === 0) {
      return (
        <div style={{ marginBottom: 8, ...style }}>
          <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(true); }} title="Tag the type, department, office and jurisdiction this belongs to" style={collapsedChip(theme.color.faint)}>
            + Tag
          </button>
        </div>
      );
    }
    return (
      <div style={{ marginBottom: 8, ...style }}>
        <button type="button" onClick={(e) => { e.stopPropagation(); setExpanded(true); }} title={names.join(", ")} aria-label={`Tags: ${names.join(", ")}`} style={collapsedChip(theme.color.muted)}>
          Tags ({total})
        </button>
      </div>
    );
  }

  return (
    <div style={{ position: "relative", marginBottom: 8, ...style }}>
      <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
        {types.map((t) => <TypeChip key={t.value} spec={t} />)}
        {depts.map((d) => <DeptChip key={d.id} name={d.name} color={d.color} />)}
        {sites.map((s) => <SiteChip key={s.id} site={s} />)}
        {countries.map((c) => <JurisdictionChip key={c} country={c} />)}
        {legacyTags?.map((t) => (
          <span key={t} title="A free-text tag from before the platform had one way of filing things. Re-tag it above and it will go." style={{ ...chipBase, background: "transparent", color: theme.color.faint, border: `1px dashed ${theme.color.border}` }}>{t}</span>
        ))}
        {canEdit && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
            title="Tag the type, department, office and jurisdiction this belongs to"
            style={{
              padding: "2px 8px", borderRadius: 3, fontSize: 11, cursor: "pointer",
              border: `1px dashed ${open ? accent : theme.color.borderStrong}`,
              background: open ? accent + "11" : "transparent",
              color: open ? accent : theme.color.faint,
            }}
          >
            {total === 0 ? "+ Tag" : "Edit tags"}
          </button>
        )}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setOpen(false); setExpanded(false); }}
          style={{ padding: "2px 6px", border: "none", background: "transparent", fontSize: 11, cursor: "pointer", color: theme.color.faint }}
        >
          Hide
        </button>
      </div>
      {open && canEdit && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 50,
            ...floatingSheet,
            padding: "8px 4px", display: "flex", gap: 8, flexWrap: "wrap", maxHeight: 320, overflowY: "auto",
          }}
        >
          {showType && (
            <PickerSection title="Type" empty="" suggested={suggested}
              items={data.types.map((t) => ({ value: t.value, label: t.label, hint: t.hint, color: t.color }))}
              checked={typeKeys} onToggle={(v) => toggleEntityTag(ref, "type", v)} />
          )}
          {showDept && (
            <PickerSection title="Department" empty="Add departments on the Departments page."
              items={data.departments.map((d) => ({ value: d.id, label: d.name, color: d.color }))}
              checked={deptIds} onToggle={(v) => toggleEntityTag(ref, "department", v)} />
          )}
          {showSite && (
            <PickerSection title="Office" empty="Add offices on the Sites page."
              items={data.sites.map((s) => ({ value: s.id, label: s.name, hint: s.country }))}
              checked={siteIds} onToggle={(v) => toggleEntityTag(ref, "site", v, singleSite)} />
          )}
          {showJurisdiction && (
            <PickerSection title="Jurisdiction" empty="Operating Locations on the organisation profile list the jurisdictions."
              items={data.countries.map((c) => ({ value: c, label: c }))}
              checked={countries} onToggle={(v) => toggleEntityTag(ref, "jurisdiction", v)} />
          )}
          <div style={{ width: "100%", borderTop: `1px solid ${theme.color.border}`, marginTop: 4, paddingTop: 4, textAlign: "right", paddingRight: 8 }}>
            <button type="button" onClick={() => setOpen(false)} style={{ fontSize: 11, color: theme.color.muted, background: "none", border: "none", cursor: "pointer" }}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Back-compatible wrapper — every assessment page already mounts this shape. */
export function ControlTagsRow({ controlId, ...rest }: Omit<EntityTagsRowProps, "entity"> & { controlId: string }) {
  return <EntityTagsRow entity={{ kind: "control", id: controlId }} {...rest} />;
}

// ── Related items ─────────────────────────────────────────────────────────────

export interface RelatedEntity {
  ref: string;
  kind: EntityKind;
  id: string;
  label: string;
  hint?: string;
  shared: Array<{ dimension: TagDimension; value: string }>;
}

export const KIND_LABELS: Record<EntityKind, string> = {
  control: "Control", risk: "Risk", asset: "Asset", supplier: "Supplier", incident: "Incident",
  exercise: "Exercise", training: "Training", document: "Evidence", ropa: "Processing activity",
  dpia: "DPIA", breach: "Breach", request: "Data subject request", action: "Corrective action",
  ai_system: "AI system", task: "Task", bia: "Business impact analysis",
  ms_context: "Context entry", ms_objective: "Objective", ms_audit: "Internal audit", ms_review: "Management review",
  ms_competence: "Competence", ms_aspect: "Environmental aspect", ms_hazard: "OH&S hazard", ms_obligation: "Compliance obligation",
  change: "Change",
};

/** Where a related row lives, so the panel is a way through rather than a list of names. */
export const KIND_HREFS: Record<EntityKind, (id: string) => string | null> = {
  control:   () => null,
  risk:      (id) => `/risk-register?risk=${encodeURIComponent(id)}`,
  asset:     (id) => `/resilience/assets?asset=${encodeURIComponent(id)}`,
  supplier:  (id) => `/tprm?supplier=${encodeURIComponent(id)}`,
  incident:  (id) => `/resilience/incidents?incident=${encodeURIComponent(id)}`,
  exercise:  (id) => `/resilience/exercises?exercise=${encodeURIComponent(id)}`,
  training:  (id) => `/resilience/training?record=${encodeURIComponent(id)}`,
  document:  (id) => `/documents?document=${encodeURIComponent(id)}`,
  ropa:      (id) => `/gdpr?tab=ropa&entry=${encodeURIComponent(id)}`,
  dpia:      (id) => `/gdpr?tab=dpia&record=${encodeURIComponent(id)}`,
  breach:    (id) => `/gdpr?tab=breaches&breach=${encodeURIComponent(id)}`,
  request:   (id) => `/gdpr?tab=dsr&request=${encodeURIComponent(id)}`,
  action:    (id) => `/improvement?action=${encodeURIComponent(id)}`,
  ai_system: (id) => `/ai-systems?system=${encodeURIComponent(id)}`,
  task:      (id) => `/schedule?event=${encodeURIComponent(id)}`,
  bia:       (id) => `/resilience/bia?bia=${encodeURIComponent(id)}`,
  ms_context:    (id) => `/management-system/context?entry=${encodeURIComponent(id)}`,
  ms_objective:  (id) => `/management-system/objectives?objective=${encodeURIComponent(id)}`,
  ms_audit:      (id) => `/management-system/audits?audit=${encodeURIComponent(id)}`,
  ms_review:     (id) => `/management-system/reviews?review=${encodeURIComponent(id)}`,
  ms_competence: (id) => `/management-system/competence?requirement=${encodeURIComponent(id)}`,
  ms_aspect:     (id) => `/management-system/aspects?aspect=${encodeURIComponent(id)}`,
  ms_hazard:     (id) => `/management-system/hazards?hazard=${encodeURIComponent(id)}`,
  ms_obligation: (id) => `/management-system/obligations?obligation=${encodeURIComponent(id)}`,
  change:        (id) => `/changes?change=${encodeURIComponent(id)}`,
};

/**
 * Everything filed the same way as this row, closest first. This is what turns
 * the tags from labels into cross-references: the risks raised against the same
 * office, the suppliers that serve the same department, the controls that answer
 * the same jurisdiction. Nothing here is a permission — it lists only rows the
 * caller's own organisation holds, because the server resolves nothing else.
 */
export function RelatedEntities({ entity, limit = 8, accentColor }: { entity: EntityRef; limit?: number; accentColor?: string }) {
  const [rows, setRows] = useState<RelatedEntity[] | null>(null);
  const [expanded, setExpanded] = useState(false);
  const data = useEntityTagData();
  const ref = useMemo(() => entityRef(entity), [entity.kind, entity.id]);
  const accent = accentColor ?? theme.color.accent;

  const reload = useCallback(() => {
    api.get<{ related: RelatedEntity[] }>(`/tags/related/${encodeURIComponent(ref)}`)
      .then((d) => setRows(d.related ?? []))
      .catch(() => setRows([]));
  }, [ref]);

  useEffect(() => { reload(); }, [reload, data.tags[ref]]);

  if (!rows || rows.length === 0) return null;
  const shown = expanded ? rows : rows.slice(0, limit);

  const valueLabel = (s: { dimension: TagDimension; value: string }) => {
    if (s.dimension === "department") return data.departments.find((d) => d.id === s.value)?.name ?? "department";
    if (s.dimension === "site") return data.sites.find((x) => x.id === s.value)?.name ?? "office";
    if (s.dimension === "type") return data.types.find((t) => t.value === s.value)?.label ?? "type";
    return s.value;
  };

  return (
    <div style={{ marginTop: 12, borderTop: `1px solid ${theme.color.border}`, paddingTop: 10 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
        Related — filed the same way
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {shown.map((r) => {
          const href = KIND_HREFS[r.kind](r.id);
          const body = (
            <>
              <span style={{ fontSize: 10, fontWeight: 700, color: theme.color.faint, minWidth: 88 }}>{KIND_LABELS[r.kind]}</span>
              <span style={{ fontSize: 12, color: theme.color.text }}>{r.label}</span>
              {r.hint && <span style={{ fontSize: 10, color: theme.color.faint }}>{r.hint}</span>}
              <span style={{ fontSize: 10, color: theme.color.muted, marginLeft: "auto" }}>
                {Array.from(new Set(r.shared.map(valueLabel))).slice(0, 3).join(" · ")}
              </span>
              {href && <ExternalLink size={11} style={{ color: accent, flexShrink: 0 }} />}
            </>
          );
          const style: React.CSSProperties = { display: "flex", alignItems: "center", gap: 8, padding: "3px 0", textDecoration: "none" };
          return href
            ? <a key={r.ref} href={href} style={style}>{body}</a>
            : <div key={r.ref} style={style}>{body}</div>;
        })}
      </div>
      {rows.length > limit && (
        <button type="button" onClick={() => setExpanded(!expanded)} style={{ marginTop: 6, fontSize: 11, color: accent, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
          {expanded ? "Show fewer" : `Show all ${rows.length}`}
        </button>
      )}
    </div>
  );
}
